// File: src/DashboardFilters.js
import React, { useState, useMemo, useEffect } from "react";
import "./App.css";

/**
 * Filters component
 * - Implements Green Loading Bar for Lists (Fetching Financial Years & States)
 * - Implements Blue Loading Bar for Apply (Generating Report)
 * - Implements Orange Loading Bar for Export (Downloading Excel)
 */
export function Filters({
  lists = {},
  filters,
  setFilters,
  onApply,
  onExport,
  compact,
  isApplying,
  isExporting,   // <--- Received from App.js
  applyPercent,
  exportPercent, // <--- Received from App.js
  isLoading      // <--- True when fetching initial dropdown lists
}) {
  
  // Safe list accessor
  const get = (k) => lists[k] || [];

  // Local state for the green list loading bar animation
  const [listLoadingPercent, setListLoadingPercent] = useState(0);

  // Effect to animate the green bar based on isLoading prop
  useEffect(() => {
    let interval;
    if (isLoading) {
      // Start animation: Reset to 10% immediately
      setListLoadingPercent(10);
      interval = setInterval(() => {
        setListLoadingPercent((prev) => (prev < 90 ? prev + 5 : prev));
      }, 200);
    } else {
      // Finish animation smoothly
      setListLoadingPercent((prev) => {
        if (prev > 0) {
          setTimeout(() => setListLoadingPercent(0), 500);
          return 100;
        }
        return prev;
      });
    }
    return () => clearInterval(interval);
  }, [isLoading]);

  // Helper to render options based on loading state
  const renderOptions = (listKey) => {
    if (isLoading) {
      return <option>Loading...</option>;
    }
    return (
      <>
        <option value="ALL">ALL</option>
        {get(listKey).map((x) => (
          <option key={x} value={x}>{x}</option>
        ))}
      </>
    );
  };

  return (
    <div className={`card filters-card no-stretch ${compact ? "compact" : ""}`}>

      {/* --- LIST LOADING BAR (Green - Initial Load) --- */}
      <div style={{
        height: listLoadingPercent > 0 ? '4px' : '0px',
        background: '#e0e0e0',
        transition: 'height 0.2s',
        overflow: 'hidden',
        marginBottom: listLoadingPercent > 0 ? 10 : 0
      }}>
        <div style={{
          width: `${listLoadingPercent}%`,
          height: '100%',
          background: '#28a745',
          transition: 'width 0.2s ease'
        }} />
      </div>

      {isLoading && (
        <div style={{
          fontSize: '12px',
          color: '#28a745',
          marginBottom: 10,
          textAlign: 'center'
        }}>
          Fetching Financial Years & States...
        </div>
      )}
      {/* ---------------------------------- */}

      <div className="filters-header">
        <h3>Filters</h3>
      </div>

      <div className="filters-grid no-stretch">
        
        {/* FROM YEAR */}
        <div className="field">
          <label>From Year</label>
          <select
            disabled={isLoading}
            value={filters.from_year || "ALL"}
            onChange={(e) =>
              setFilters((f) => ({ ...f, from_year: e.target.value }))
            }
          >
            {renderOptions("yearRange")}
          </select>
        </div>

        {/* TO YEAR */}
        <div className="field">
          <label>To Year</label>
          <select
            disabled={isLoading}
            value={filters.to_year || "ALL"}
            onChange={(e) =>
              setFilters((f) => ({ ...f, to_year: e.target.value }))
            }
          >
            {renderOptions("yearRange")}
          </select>
        </div>

        {/* FINANCE YEAR */}
        <div className="field">
          <label>Finance Year (Exact)</label>
          <select
            disabled={isLoading}
            value={filters.finance_year}
            onChange={(e) =>
              setFilters((f) => ({ ...f, finance_year: e.target.value }))
            }
          >
            {renderOptions("financeYears")}
          </select>
        </div>

        {/* MONTH / YEAR */}
        <div className="field">
          <label>Month / Year</label>
          <select
            disabled={isLoading}
            value={filters.month_year}
            onChange={(e) =>
              setFilters((f) => ({ ...f, month_year: e.target.value }))
            }
          >
            {renderOptions("months")}
          </select>
        </div>

        {/* STATE (Student State) */}
        <div className="field">
          <label>State</label>
          <select
            disabled={isLoading}
            value={filters.state}
            onChange={(e) =>
              setFilters((f) => ({ ...f, state: e.target.value }))
            }
          >
            {renderOptions("states")}
          </select>
        </div>

        {/* UNIVERSITY STATE (New Filter) */}
        <div className="field">
          <label>University State</label>
          <select
            disabled={isLoading}
            value={filters.university_state || "ALL"} 
            onChange={(e) =>
              setFilters((f) => ({ ...f, university_state: e.target.value }))
            }
          >
            {/* Ideally uses a separate list, fallback to 'states' if same */}
            {renderOptions("university_states") || renderOptions("states")}
          </select>
        </div>

        {/* UNIVERSITY */}
        <div className="field">
          <label>University</label>
          <select
            disabled={isLoading}
            value={filters.university}
            onChange={(e) =>
              setFilters((f) => ({ ...f, university: e.target.value }))
            }
          >
            {renderOptions("universities")}
          </select>
        </div>

        {/* CATEGORY */}
        <div className="field">
          <label>Category</label>
          <select
            disabled={isLoading}
            value={filters.category}
            onChange={(e) =>
              setFilters((f) => ({ ...f, category: e.target.value }))
            }
          >
            {renderOptions("categories")}
          </select>
        </div>

        {/* HANDICAPPED */}
        <div className="field">
          <label>Handicaped</label>
          <select
            disabled={isLoading}
            value={filters.handicaped}
            onChange={(e) =>
              setFilters((f) => ({ ...f, handicaped: e.target.value }))
            }
          >
            {renderOptions("handicaped")}
          </select>
        </div>

        {/* GENDER */}
        <div className="field">
          <label>Gender</label>
          <select
            disabled={isLoading}
            value={filters.gender}
            onChange={(e) =>
              setFilters((f) => ({ ...f, gender: e.target.value }))
            }
          >
            {renderOptions("genders")}
          </select>
        </div>

        {/* SCHEME */}
        <div className="field">
          <label>Scheme</label>
          <select
            disabled={isLoading}
            value={filters.scheme}
            onChange={(e) =>
              setFilters((f) => ({ ...f, scheme: e.target.value }))
            }
          >
            {renderOptions("schemes")}
          </select>
        </div>

        {/* STREAM */}
        <div className="field">
          <label>Stream</label>
          <select
            disabled={isLoading}
            value={filters.stream}
            onChange={(e) =>
              setFilters((f) => ({ ...f, stream: e.target.value }))
            }
          >
            {renderOptions("streams")}
          </select>
        </div>

        {/* SUBJECT */}
        <div className="field">
          <label>Subject</label>
          <select
            disabled={isLoading}
            value={filters.subject}
            onChange={(e) =>
              setFilters((f) => ({ ...f, subject: e.target.value }))
            }
          >
            {renderOptions("subjects")}
          </select>
        </div>

        {/* PERIOD */}
        <div className="field">
          <label>Period</label>
          <select
            disabled={isLoading}
            value={filters.period}
            onChange={(e) =>
              setFilters((f) => ({ ...f, period: e.target.value }))
            }
          >
            {renderOptions("periods")}
          </select>
        </div>

        {/* STUDENT ID */}
        <div className="field">
          <label>Student ID</label>
          <input
            disabled={isLoading}
            placeholder={isLoading ? "Loading..." : ""}
            value={filters.student_id || ""}
            onChange={(e) =>
              setFilters((f) => ({ ...f, student_id: e.target.value }))
            }
          />
        </div>

        {/* STUDENT NAME */}
        <div className="field">
          <label>Student Name</label>
          <input
            disabled={isLoading}
            placeholder={isLoading ? "Loading..." : ""}
            value={filters.student_name || ""}
            onChange={(e) =>
              setFilters((f) => ({ ...f, student_name: e.target.value }))
            }
          />
        </div>

        {/* SEARCH */}
        <div className="field">
          <label>Search (All Fields)</label>
          <input
            disabled={isLoading}
            placeholder={isLoading ? "Loading..." : ""}
            value={filters.q || ""}
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
          />
        </div>
      </div>

      <div className="filters-actions">
        <button
          className="btn primary"
          onClick={onApply}
          disabled={isApplying || isExporting || isLoading}
        >
          {isApplying ? `Applying (${applyPercent}%)` : "Apply Filters"}
        </button>

        <button
          className="btn outline"
          onClick={onExport}
          disabled={isExporting || isApplying || isLoading}
        >
          {isExporting ? `Exporting (${exportPercent}%)` : "Export Excel"}
        </button>
      </div>

      {/* --- APPLY LOADING BAR (Blue) --- */}
      {isApplying && (
        <div className="inline-progress" style={{ marginTop: 15 }}>
          <div style={{ marginBottom: 6, fontSize: '0.9rem', color: '#1976d2' }}>
            <strong>Generating Report... {applyPercent}%</strong>
          </div>
          <div
            style={{
              width: "100%",
              height: 8,
              background: "#e3f2fd",
              borderRadius: 6,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${applyPercent}%`,
                height: "100%",
                background: "#1976d2",
                transition: "width 0.2s ease",
              }}
            />
          </div>
        </div>
      )}

      {/* --- EXPORT LOADING BAR (Orange) --- */}
      {isExporting && (
        <div className="inline-progress" style={{ marginTop: 15 }}>
          <div style={{ marginBottom: 6, fontSize: '0.9rem', color: '#e65100' }}>
            <strong>Preparing Excel File... {exportPercent}%</strong>
          </div>
          <div
            style={{
              width: "100%",
              height: 8,
              background: "#ffe0b2",
              borderRadius: 6,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${exportPercent}%`,
                height: "100%",
                background: "#ff9800",
                transition: "width 0.2s ease",
              }}
            />
          </div>
        </div>
      )}

    </div>
  );
}

export function ResultsTable({ rows = [], pageSize = 100 }) {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");

  useEffect(() => setPage(1), [rows, search]);

  const filtered = useMemo(() => {
    if (!search) return rows;
    const s = search.toLowerCase();
    return rows.filter((r) =>
      Object.values(r).join(" ").toLowerCase().includes(s)
    );
  }, [rows, search]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = (page - 1) * pageSize;
  const pageRows = filtered.slice(start, start + pageSize);

  return (
    <div className="card results-card only-results-expand">
      <div className="results-header">
        <h3>Results Data</h3>
        <input
          className="gov-search-input"
          placeholder="Quick search in results..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="table-wrap only-results-expand">
        <table className="results-table">
          <thead>
            <tr>
              {pageRows[0] ? (
                Object.keys(pageRows[0]).map((k) => <th key={k}>{k}</th>)
              ) : (
                <th>No data</th>
              )}
            </tr>
          </thead>
          <tbody>
            {pageRows.length > 0 ? (
              pageRows.map((r, i) => (
                <tr key={i}>
                  {Object.keys(r).map((k) => (
                    <td key={k}>{r[k]}</td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="100%" style={{textAlign: "center", padding: "20px"}}>
                   No records found matching criteria.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="pager">
        <span>
          Showing {total === 0 ? 0 : start + 1}–{Math.min(start + pageSize, total)} of{" "}
          {total}
        </span>

        <div className="pager-controls">
          <button disabled={page === 1} onClick={() => setPage(1)}>«</button>
          <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}>‹</button>
          <span className="page-info">{page} / {totalPages}</span>
          <button disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>›</button>
          <button disabled={page === totalPages} onClick={() => setPage(totalPages)}>»</button>
        </div>
      </div>
    </div>
  );
}

// Optional overlay if you prefer full screen block
export function ExportOverlay({ show, message }) {
  if (!show) return null;
  return (
    <div className="overlay">
      <div className="overlay-content">
        <h3 style={{color: "#333"}}>{message}</h3>
        <div className="progress-bar large">
          <div className="progress-fill indeterminate" />
        </div>
        <p className="progress-text">
          Please wait while the system generates your file...
        </p>
      </div>
    </div>
  );
}